import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ToolLayout } from '../ToolLayout';
import { CodeEditor } from '../CodeEditor';

// A real (expired) HS256 JWT with readable claims for use as a sample
const SAMPLE_JWT = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VyQG9uZWFkdmFuY2VkLmNvbSIsIm5hbWUiOiJKb2huIERvZSIsInJvbGVzIjpbImVuZ2luZWVyIiwiYWRtaW4iXSwiaWF0IjoxNzE2MDAwMDAwLCJleHAiOjE3MTYwMDM2MDB9.placeholder_signature';

// jwt.io-style part colours
const COLOR_HEADER  = '#fb015b';
const COLOR_PAYLOAD = '#d63aff';
const COLOR_SIG     = '#00b9f1';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: theme.spacing(1.5),
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  tokenDisplay: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 12.5,
    lineHeight: 1.7,
    wordBreak: 'break-all',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    minHeight: 64,
  },
  sectionBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  },
  codeBlock: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 12.5,
    lineHeight: 1.6,
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
    color: theme.palette.text.primary,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  },
}));

interface DecodedJwt {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  status: 'valid' | 'expired' | 'no-exp';
  expiresAt: Date | null;
  issuedAt: Date | null;
  notBefore: Date | null;
  parts: [string, string, string];
}

function base64UrlDecode(str: string): string {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (padded.length % 4)) % 4;
  return atob(padded + '='.repeat(padding));
}

function decodeJwt(token: string): { result: DecodedJwt; error: null } | { result: null; error: string } {
  const parts = token.trim().split('.');
  if (parts.length !== 3) {
    return { result: null, error: 'Not a valid JWT — expected 3 dot-separated parts.' };
  }
  try {
    const header = JSON.parse(base64UrlDecode(parts[0]));
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const now = Date.now() / 1000;
    const expiresAt = payload.exp ? new Date(payload.exp * 1000) : null;
    const issuedAt = payload.iat ? new Date(payload.iat * 1000) : null;
    const notBefore = payload.nbf ? new Date(payload.nbf * 1000) : null;
    let status: DecodedJwt['status'] = 'no-exp';
    if (payload.exp) {
      status = payload.exp < now ? 'expired' : 'valid';
    }
    return { result: { header, payload, status, expiresAt, issuedAt, notBefore, parts: [parts[0], parts[1], parts[2]] }, error: null };
  } catch (e: any) {
    return { result: null, error: `Failed to decode JWT: ${e?.message}` };
  }
}

function StatusBadge({ status }: { status: DecodedJwt['status'] }) {
  if (status === 'valid') return <Chip icon={<CheckCircleIcon />} label="Valid (not expired)" color="success" size="small" variant="outlined" />;
  if (status === 'expired') return <Chip icon={<ErrorIcon />} label="Expired" color="error" size="small" variant="outlined" />;
  return <Chip icon={<WarningAmberIcon />} label="No expiry claim" color="warning" size="small" variant="outlined" />;
}

export function JwtDecoderPage() {
  const { classes } = useStyles();
  const [token, setToken] = useState('');
  const [decoded, setDecoded] = useState<DecodedJwt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedHeader, setCopiedHeader] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);

  const decode = useCallback((value: string) => {
    if (!value.trim()) { setDecoded(null); setError(null); return; }
    const { result, error: err } = decodeJwt(value);
    if (err) { setError(err); setDecoded(null); }
    else { setDecoded(result); setError(null); }
  }, []);

  const handleChange = (value: string) => {
    setToken(value);
    decode(value);
  };

  const clear = () => { setToken(''); setDecoded(null); setError(null); setCopiedHeader(false); setCopiedPayload(false); };

  const loadSample = () => handleChange(SAMPLE_JWT);

  const copyHeader = () => {
    if (!decoded) return;
    navigator.clipboard.writeText(JSON.stringify(decoded.header, null, 2));
    setCopiedHeader(true);
    setTimeout(() => setCopiedHeader(false), 2000);
  };

  const copyPayload = () => {
    if (!decoded) return;
    navigator.clipboard.writeText(JSON.stringify(decoded.payload, null, 2));
    setCopiedPayload(true);
    setTimeout(() => setCopiedPayload(false), 2000);
  };

  return (
    <ToolLayout
      title="JWT Decoder"
      subtitle="Inspect a JWT token — header, payload and expiry status"
    >
      <Box className={classes.root}>
        {/* ── Left: token input ── */}
        <Box className={classes.pane}>
          <Box className={classes.paneHeader}>
            <Typography className={classes.paneLabel}>Encoded Token</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12 }}>
                Load sample
              </Button>
              <Tooltip title="Clear">
                <span>
                  <IconButton size="small" onClick={clear} disabled={!token}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <CodeEditor
            value={token}
            onChange={handleChange}
            rows={8}
            placeholder="Paste a JWT here…"
          />

          {/* Colour-coded token breakdown */}
          {decoded && (
            <>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Token parts
              </Typography>
              <Box className={classes.tokenDisplay}>
                <span style={{ color: COLOR_HEADER }}>{decoded.parts[0]}</span>
                <Box component="span" sx={{ color: 'text.disabled' }}>.</Box>
                <span style={{ color: COLOR_PAYLOAD }}>{decoded.parts[1]}</span>
                <Box component="span" sx={{ color: 'text.disabled' }}>.</Box>
                <span style={{ color: COLOR_SIG }}>{decoded.parts[2]}</span>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: COLOR_HEADER, fontWeight: 700 }}>■ Header</Typography>
                <Typography variant="caption" sx={{ color: COLOR_PAYLOAD, fontWeight: 700 }}>■ Payload</Typography>
                <Typography variant="caption" sx={{ color: COLOR_SIG, fontWeight: 700 }}>■ Signature</Typography>
              </Box>
            </>
          )}

          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>

        {/* ── Right: decoded sections ── */}
        <Box className={classes.pane}>
          <Box className={classes.paneHeader}>
            <Typography className={classes.paneLabel}>Decoded</Typography>
          </Box>

          {!decoded && !error && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Paste a token on the left to decode it.
            </Typography>
          )}

          {decoded && (
            <>
              {/* Status */}
              <Box className={classes.statusRow}>
                <StatusBadge status={decoded.status} />
                {decoded.expiresAt && (
                  <Typography variant="body2" color="textSecondary">
                    Expires: <strong>{decoded.expiresAt.toLocaleString()}</strong>
                  </Typography>
                )}
                {decoded.issuedAt && (
                  <Typography variant="body2" color="textSecondary">
                    Issued: <strong>{decoded.issuedAt.toLocaleString()}</strong>
                  </Typography>
                )}
                {decoded.notBefore && (
                  <Typography variant="body2" color="textSecondary">
                    Not before: <strong>{decoded.notBefore.toLocaleString()}</strong>
                  </Typography>
                )}
              </Box>

              {/* Header */}
              <Box className={classes.sectionBox}>
                <Box className={classes.sectionHeader}>
                  <Typography className={classes.sectionTitle} sx={{ color: COLOR_HEADER }}>Header</Typography>
                  <Tooltip title={copiedHeader ? 'Copied!' : 'Copy header'}>
                    <IconButton size="small" onClick={copyHeader}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <pre className={classes.codeBlock}>{JSON.stringify(decoded.header, null, 2)}</pre>
              </Box>

              <Divider />

              {/* Payload */}
              <Box className={classes.sectionBox}>
                <Box className={classes.sectionHeader}>
                  <Typography className={classes.sectionTitle} sx={{ color: COLOR_PAYLOAD }}>Payload</Typography>
                  <Tooltip title={copiedPayload ? 'Copied!' : 'Copy payload'}>
                    <IconButton size="small" onClick={copyPayload}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <pre className={classes.codeBlock}>{JSON.stringify(decoded.payload, null, 2)}</pre>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ToolLayout>
  );
}
